import { useEffect, useState, useSyncExternalStore } from "react";

// What the user is looking at, for the AI Assistant. The chat window
// (ChatbotWidget) sends it with each question, while the user leaves it on,
// so that "this meter", "these leaks" or "this period" are what is on the
// screen, and a question that names no period or category is answered for
// the page's (python/flask_api/agentic_ai/page_context.py).
//
// A page publishes what it shows with usePageContext - and so can a part of
// one, a tab or a map pop-up. The window starts from the page's name and the
// district (by the route), then takes the parts in the order they first
// rendered, the page before its parts: a later part's page, section, meter,
// meters and accounts replace an earlier one's, its filters are added (by
// label - an empty value takes one out), its summary is added to theirs.
//
// A part's context, every field optional:
//   page      the page's name, as its heading or the sidebar has it
//   section   the part of it in view: a tab, a view
//   filters   { label: value } as the page shows them; a period as its
//             name ("Past Month") or its dates (periodOf), under "Period"
//   meter     the meter in focus: its detail page, an open pop-up
//   meters    the meters on screen, in the page's order (the first
//             MAX_METERS are sent, with meters_total when there are more)
//   meters_total  how many there are, when meters holds only some
//   accounts  the accounts selected
//   summary   a line of what it shows - its headline figures

export const MAX_METERS = 50;
const MAX_ACCOUNTS = 20;
const MAX_FILTERS = 12;

let parts = [];
let order = 0;
const listeners = new Set();

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

function publish(id, context) {
  const others = parts.filter((part) => part.id !== id);
  parts = context ? [...others, { id, context }].sort((a, b) => a.id - b.id) : others;
  listeners.forEach((listener) => listener());
}

// A page, or a part of one, telling the assistant what it shows (null:
// nothing for now) - until it changes or leaves the screen.
export function usePageContext(context) {
  // Its place among the parts: where it first rendered, after the page
  // around it.
  const [id] = useState(() => (order += 1));
  // Published when what it says changes - not each time it renders.
  const key = context ? JSON.stringify(context) : "";
  useEffect(() => {
    if (!key) return undefined;
    publish(id, JSON.parse(key));
    return () => publish(id, null);
  }, [id, key]);
}

// The parts published, in order ({ id, context }).
export const usePageParts = () => useSyncExternalStore(subscribe, () => parts);

const filled = (value) =>
  value !== undefined && value !== null && value !== "" && !(Array.isArray(value) && !value.length);

// The page's context as the window sends it: the base (the page's name and
// the district) with the parts on it - or null when there is nothing.
export function mergePageContext(base, list = []) {
  const merged = {};
  const filters = {};
  const summaries = [];
  for (const context of [base, ...list.map((part) => part.context)]) {
    if (!context || typeof context !== "object") continue;
    for (const key of ["page", "section", "district", "meter"]) {
      if (filled(context[key])) merged[key] = String(context[key]);
    }
    if (filled(context.meters) && Array.isArray(context.meters)) {
      merged.meters = [...new Set(context.meters.filter(filled).map(String))];
      merged.meters_total = Number.isInteger(context.meters_total) ? context.meters_total : merged.meters.length;
    }
    if (filled(context.accounts) && Array.isArray(context.accounts)) {
      merged.accounts = [...new Set(context.accounts.filter(filled).map(String))];
    }
    for (const [label, value] of Object.entries(context.filters || {})) {
      if (filled(value)) filters[label] = Array.isArray(value) ? value.join(", ") : String(value);
      else delete filters[label];
    }
    if (filled(context.summary)) summaries.push(String(context.summary));
  }
  const labels = Object.keys(filters).slice(0, MAX_FILTERS);
  if (labels.length) merged.filters = Object.fromEntries(labels.map((label) => [label, filters[label]]));
  if (merged.meters) {
    const total = Math.max(merged.meters_total, merged.meters.length);
    merged.meters = merged.meters.slice(0, MAX_METERS);
    if (total > merged.meters.length) merged.meters_total = total;
    else delete merged.meters_total;
  }
  if (merged.accounts) merged.accounts = merged.accounts.slice(0, MAX_ACCOUNTS);
  if (summaries.length) merged.summary = summaries.join("; ");
  return Object.keys(merged).length ? merged : null;
}

// A day as YYYY-MM-DD, in local time - from a Date, a time or a date's text;
// null for none.
export function isoDay(value) {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

// A period for the filters: its name ("Past Month") - or, a custom one, its
// dates ("2026-10-01 to 2026-10-15"), which the assistant can ask for.
export function periodOf(name, start, end) {
  const from = isoDay(start);
  const to = isoDay(end);
  if (from && to) return from === to ? from : `${from} to ${to}`;
  return name || null;
}

const countOf = (n, word) => `${n.toLocaleString()} ${word}${n === 1 ? "" : "s"}`;

// The context in a few words, for the chip by the message box:
// "Leak Flow · Past Month · 12 meters".
export function contextLabel(context) {
  if (!context) return "";
  const where = context.page && context.section ? `${context.page} · ${context.section}` : context.page || context.section;
  const period = context.filters?.Period;
  const meters = context.meters?.length ? countOf(context.meters_total || context.meters.length, "meter") : null;
  return [where, period, context.meter ? `Meter ${context.meter}` : meters].filter(Boolean).join(" · ");
}

// The context line by line ([label, value]), for its details: what is sent.
export function contextRows(context) {
  if (!context) return [];
  const rows = [];
  if (context.page) rows.push(["Page", context.section ? `${context.page} · ${context.section}` : context.page]);
  if (context.district) rows.push(["District", context.district]);
  for (const [label, value] of Object.entries(context.filters || {})) rows.push([label, value]);
  if (context.meter) rows.push(["Meter in focus", context.meter]);
  if (context.meters?.length) {
    const total = context.meters_total || context.meters.length;
    const shown = context.meters.slice(0, 3).join(", ");
    rows.push(["Meters", `${total.toLocaleString()} (${shown}${total > 3 ? ", …" : ""})`]);
  }
  if (context.accounts?.length) rows.push(["Accounts", context.accounts.join(", ")]);
  if (context.summary) rows.push(["Showing", context.summary]);
  return rows;
}

// Questions to start from on this page: about the meter in focus, or what the
// page shows.
export function pageQuestions(context, investigating) {
  if (!context?.page) return [];
  if (context.meter) return [investigating ? `Investigate meter ${context.meter}` : `What's going on with meter ${context.meter}?`];
  return [investigating ? "Investigate what's on this page" : "Summarize what I'm looking at"];
}
